import type {
  UserDetailsResponse,
  UserFollowersResponse,
  UserFollowingResponse,
} from "./interfaces";
import { ContentType, ConsumeType, FavouriteType } from "../../constants/enums";

/**
 * Mock user followers list
 */
export const MOCK_USER_FOLLOWERS: UserFollowersResponse = {
  page: 1,
  pageSize: 10,
  total: 1247,
  items: [
    {
      userId: "follower-1",
      username: "CinephileJane",
      avatarUrl: "https://i.pravatar.cc/150?img=1",
    },
    {
      userId: "follower-2",
      username: "MovieBuff99",
      avatarUrl: "https://i.pravatar.cc/150?img=2",
    },
    {
      userId: "follower-3",
      username: "FilmCritic_01",
      avatarUrl: "https://i.pravatar.cc/150?img=3",
    },
    {
      userId: "follower-4",
      username: "SeriesAddict",
      avatarUrl: "https://i.pravatar.cc/150?img=4",
    },
    {
      userId: "follower-5",
      username: "DirectorFan",
      avatarUrl: "https://i.pravatar.cc/150?img=5",
    },
    {
      userId: "follower-6",
      username: "ScreenWriter",
      avatarUrl: "https://i.pravatar.cc/150?img=6",
    },
    {
      userId: "follower-7",
      username: "ActorStalker",
      avatarUrl: "https://i.pravatar.cc/150?img=7",
    },
    {
      userId: "follower-8",
      username: "CinemaParadiso",
      avatarUrl: "https://i.pravatar.cc/150?img=8",
    },
    {
      userId: "follower-9",
      username: "PopcornLover",
      avatarUrl: "https://i.pravatar.cc/150?img=9",
    },
    {
      userId: "follower-10",
      username: "IndieFilmFan",
      avatarUrl: "https://i.pravatar.cc/150?img=10",
    },
  ],
};

/**
 * Mock user following list
 */
export const MOCK_USER_FOLLOWING: UserFollowingResponse = {
  page: 1,
  pageSize: 10,
  total: 89,
  items: [
    {
      userId: "following-1",
      username: "KojimaProductions",
      avatarUrl: "https://i.pravatar.cc/150?img=11",
    },
    {
      userId: "following-2",
      username: "NolanFanClub",
      avatarUrl: "https://i.pravatar.cc/150?img=12",
    },
    {
      userId: "following-3",
      username: "TarantinoFeet",
      avatarUrl: "https://i.pravatar.cc/150?img=13",
    },
    {
      userId: "following-4",
      username: "A24_Official",
      avatarUrl: "https://i.pravatar.cc/150?img=14",
    },
    {
      userId: "following-5",
      username: "HBO_Max",
      avatarUrl: "https://i.pravatar.cc/150?img=15",
    },
  ],
};

/**
 * Mock user details for testing
 */
export const MOCK_USER_DETAILS: UserDetailsResponse = {
  // Header info
  targetUserId: "user-123",
  targetUserName: "MovieLover42",
  targetUserImageLink: "https://i.pravatar.cc/300?img=12",
  bio: "Passionate about cinema and literature. Always looking for hidden gems and underrated masterpieces.",

  // Follow info
  isFollowing: false,
  followersCount: 1247,
  followingCount: 89,

  // Statistics
  stats: {
    reviewsCount: 156,
    collectionsCount: 23,
    commentsCount: 432,
    favouritesCount: 27,
    watchedCount: {
      [ContentType.MOVIE]: 287,
      [ContentType.TV_SHOW]: 45,
      [ContentType.BOOK]: 134,
      [ContentType.COMICS]: 67,
      [ContentType.GAME]: 89,
    },
    spentTime: {
      [ContentType.MOVIE]: 34560, // ~576 hours
      [ContentType.TV_SHOW]: 21600, // ~360 hours
      [ContentType.BOOK]: 42500, // pages
      [ContentType.COMICS]: 8900, // pages
      [ContentType.GAME]: 15840, // ~264 hours
    },
  },

  // Consume content - Movies watched
  consumeContent: {
    [ContentType.MOVIE]: {
      [ConsumeType.WATCHED]: {
        items: [
          {
            id: "content-1",
            name: "The Shawshank Redemption",
            originalName: "The Shawshank Redemption",
            imageUrl: "https://picsum.photos/seed/movie1/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 9.3,
            releaseDate: "1994-09-23",
            userRating: 10,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-2",
            name: "The Godfather",
            originalName: "The Godfather",
            imageUrl: "https://picsum.photos/seed/movie2/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 9.2,
            releaseDate: "1972-03-24",
            userRating: 9,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-3",
            name: "The Dark Knight",
            originalName: "The Dark Knight",
            imageUrl: "https://picsum.photos/seed/movie3/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 9.0,
            releaseDate: "2008-07-18",
            userRating: 10,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-4",
            name: "Pulp Fiction",
            originalName: "Pulp Fiction",
            imageUrl: "https://picsum.photos/seed/movie4/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 8.9,
            releaseDate: "1994-10-14",
            userRating: 9,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-5",
            name: "Forrest Gump",
            originalName: "Forrest Gump",
            imageUrl: "https://picsum.photos/seed/movie5/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 8.8,
            releaseDate: "1994-07-06",
            userRating: 8,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-6",
            name: "Inception",
            originalName: "Inception",
            imageUrl: "https://picsum.photos/seed/movie6/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 8.8,
            releaseDate: "2010-07-16",
            userRating: 9,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-7",
            name: "Fight Club",
            originalName: "Fight Club",
            imageUrl: "https://picsum.photos/seed/movie7/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 8.8,
            releaseDate: "1999-10-15",
            userRating: 10,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-8",
            name: "The Matrix",
            originalName: "The Matrix",
            imageUrl: "https://picsum.photos/seed/movie8/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 8.7,
            releaseDate: "1999-03-31",
            userRating: 9,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-9",
            name: "Interstellar",
            originalName: "Interstellar",
            imageUrl: "https://picsum.photos/seed/movie9/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 8.6,
            releaseDate: "2014-11-07",
            userRating: 10,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
          {
            id: "content-10",
            name: "Goodfellas",
            originalName: "Goodfellas",
            imageUrl: "https://picsum.photos/seed/movie10/300/450",
            contentTypeId: ContentType.MOVIE,
            averageRating: 8.7,
            releaseDate: "1990-09-19",
            userRating: 8,
            userConsumeTypeId: ConsumeType.WATCHED,
          },
        ],
        pagination: {
          currentPage: 1,
          totalPages: 29,
          totalItems: 287,
          pageSize: 10,
          hasMore: true,
        },
      },
    },
  },

  // Favourites - Content
  favourites: {
    contents: {
      items: [
        {
          id: "fav-content-1",
          name: "The Lord of the Rings: The Return of the King",
          originalName: "The Lord of the Rings: The Return of the King",
          imageUrl: "https://picsum.photos/seed/fav1/300/450",
          contentTypeId: ContentType.MOVIE,
          averageRating: 9.0,
          releaseDate: "2003-12-17",
          userRating: 10,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-2",
          name: "Breaking Bad",
          originalName: "Breaking Bad",
          imageUrl: "https://picsum.photos/seed/fav2/300/450",
          contentTypeId: ContentType.TV_SHOW,
          averageRating: 9.5,
          releaseDate: "2008-01-20",
          userRating: 10,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-3",
          name: "1984",
          originalName: "Nineteen Eighty-Four",
          imageUrl: "https://picsum.photos/seed/fav3/300/450",
          contentTypeId: ContentType.BOOK,
          averageRating: 8.8,
          releaseDate: "1949-06-08",
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-4",
          name: "Watchmen",
          originalName: "Watchmen",
          imageUrl: "https://picsum.photos/seed/fav4/300/450",
          contentTypeId: ContentType.COMICS,
          averageRating: 9.0,
          releaseDate: "1986-09-01",
          userRating: 9,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-5",
          name: "The Last of Us",
          originalName: "The Last of Us",
          imageUrl: "https://picsum.photos/seed/fav5/300/450",
          contentTypeId: ContentType.GAME,
          averageRating: 9.2,
          releaseDate: "2013-06-14",
          userRating: 10,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-6",
          name: "Dune",
          originalName: "Dune",
          imageUrl: "https://picsum.photos/seed/fav6/300/450",
          contentTypeId: ContentType.BOOK,
          averageRating: 8.5,
          releaseDate: "1965-08-01",
          userRating: 9,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-7",
          name: "The Witcher 3: Wild Hunt",
          originalName: "The Witcher 3: Wild Hunt",
          imageUrl: "https://picsum.photos/seed/fav7/300/450",
          contentTypeId: ContentType.GAME,
          averageRating: 9.3,
          releaseDate: "2015-05-19",
          userRating: 10,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-8",
          name: "Blade Runner 2049",
          originalName: "Blade Runner 2049",
          imageUrl: "https://picsum.photos/seed/fav8/300/450",
          contentTypeId: ContentType.MOVIE,
          averageRating: 8.0,
          releaseDate: "2017-10-06",
          userRating: 9,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-9",
          name: "True Detective",
          originalName: "True Detective",
          imageUrl: "https://picsum.photos/seed/fav9/300/450",
          contentTypeId: ContentType.TV_SHOW,
          averageRating: 8.9,
          releaseDate: "2014-01-12",
          userRating: 9,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "fav-content-10",
          name: "Sandman",
          originalName: "The Sandman",
          imageUrl: "https://picsum.photos/seed/fav10/300/450",
          contentTypeId: ContentType.COMICS,
          averageRating: 9.1,
          releaseDate: "1989-01-01",
          userRating: 10,
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
      ],
      pagination: {
        currentPage: 1,
        totalPages: 8,
        totalItems: 78,
        pageSize: 10,
        hasMore: true,
      },
    },
    persons: {
      items: [
        {
          id: "person-1",
          name: "Christopher Nolan",
          originalName: "Christopher Nolan",
          imageUrl: "https://i.pravatar.cc/300?img=33",
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "person-2",
          name: "Quentin Tarantino",
          originalName: "Quentin Tarantino",
          imageUrl: "https://i.pravatar.cc/300?img=15",
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "person-3",
          name: "Denis Villeneuve",
          originalName: "Denis Villeneuve",
          imageUrl: "https://i.pravatar.cc/300?img=31",
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "person-4",
          name: "Cillian Murphy",
          originalName: "Cillian Murphy",
          imageUrl: "https://i.pravatar.cc/300?img=68",
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
        {
          id: "person-5",
          name: "Margot Robbie",
          originalName: "Margot Robbie",
          imageUrl: "https://i.pravatar.cc/300?img=47",
          userFavouriteTypeId: FavouriteType.IS_FAVOURITE,
        },
      ],
      pagination: {
        currentPage: 1,
        totalPages: 2,
        totalItems: 12,
        pageSize: 10,
        hasMore: true,
      },
    },
    collections: {
      items: [
        {
          id: "collection-1",
          title: "Mind-Bending Thrillers",
          imageUrl: "https://picsum.photos/seed/coll1/400/225",
          userVoteId: 1,
          reputation: 342,
          favouriteTypeId: FavouriteType.IS_FAVOURITE,
          favouriteCount: 89,
          messageCount: 23,
          authorId: "user-456",
          authorName: "CinephileJohn",
          authorImage: "https://i.pravatar.cc/150?img=33",
        },
        {
          id: "collection-2",
          title: "Epic Fantasy Journeys",
          imageUrl: "https://picsum.photos/seed/coll2/400/225",
          userVoteId: 1,
          reputation: 567,
          favouriteTypeId: FavouriteType.IS_FAVOURITE,
          favouriteCount: 145,
          messageCount: 67,
          authorId: "user-789",
          authorName: "FantasyFan",
          authorImage: "https://i.pravatar.cc/150?img=47",
        },
      ],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 2,
        pageSize: 10,
        hasMore: false,
      },
    },
  },

  // Recent collections preview
  recentCollections: [
    {
      id: "user-coll-1",
      title: "Christopher Nolan Essentials",
      imageUrl: "https://picsum.photos/seed/usercoll1/400/225",
      userVoteId: 1,
      reputation: 892,
      favouriteTypeId: FavouriteType.UNSPECIFIED,
      favouriteCount: 234,
      messageCount: 89,
      authorId: "user-123",
      authorName: "MovieLover42",
      authorImage: "https://i.pravatar.cc/150?img=12",
    },
    {
      id: "user-coll-2",
      title: "Sci-Fi Masterpieces",
      imageUrl: "https://picsum.photos/seed/usercoll2/400/225",
      userVoteId: 1,
      reputation: 756,
      favouriteTypeId: FavouriteType.UNSPECIFIED,
      favouriteCount: 189,
      messageCount: 56,
      authorId: "user-123",
      authorName: "MovieLover42",
      authorImage: "https://i.pravatar.cc/150?img=12",
    },
    {
      id: "user-coll-3",
      title: "Hidden Indie Gems",
      imageUrl: "https://picsum.photos/seed/usercoll3/400/225",
      userVoteId: 1,
      reputation: 543,
      favouriteTypeId: FavouriteType.UNSPECIFIED,
      favouriteCount: 134,
      messageCount: 45,
      authorId: "user-123",
      authorName: "MovieLover42",
      authorImage: "https://i.pravatar.cc/150?img=12",
    },
    {
      id: "user-coll-4",
      title: "90s Classics",
      imageUrl: "https://picsum.photos/seed/usercoll4/400/225",
      userVoteId: 1,
      reputation: 421,
      favouriteTypeId: FavouriteType.UNSPECIFIED,
      favouriteCount: 98,
      messageCount: 34,
      authorId: "user-123",
      authorName: "MovieLover42",
      authorImage: "https://i.pravatar.cc/150?img=12",
    },
    {
      id: "user-coll-5",
      title: "Crime & Noir",
      imageUrl: "https://picsum.photos/seed/usercoll5/400/225",
      userVoteId: 1,
      reputation: 378,
      favouriteTypeId: FavouriteType.UNSPECIFIED,
      favouriteCount: 87,
      messageCount: 29,
      authorId: "user-123",
      authorName: "MovieLover42",
      authorImage: "https://i.pravatar.cc/150?img=12",
    },
    {
      id: "user-coll-6",
      title: "Epic Book Adaptations",
      imageUrl: "https://picsum.photos/seed/usercoll6/400/225",
      userVoteId: 1,
      reputation: 312,
      favouriteTypeId: FavouriteType.UNSPECIFIED,
      favouriteCount: 72,
      messageCount: 23,
      authorId: "user-123",
      authorName: "MovieLover42",
      authorImage: "https://i.pravatar.cc/150?img=12",
    },
  ],

  // Top reviews preview
  topReviews: [
    {
      id: "review-1",
      title: "A Masterpiece of Cinema - Shawshank Redemption Review",
      body: "An unforgettable journey through hope, friendship, and redemption. Tim Robbins and Morgan Freeman deliver career-defining performances in this adaptation of Stephen King's novella. The film's pacing is perfect, building tension gradually while never losing sight of its emotional core...",
      reputation: 456,
      messageCount: 89,
      userVoteId: 1,
      authorId: "user-123",
      authorName: "MovieBuff",
      authorImage: "https://picsum.photos/seed/user1/150/150",
      authorContentRating: 10,
      targetContentId: "content-1",
      targetContentTitle: "The Shawshank Redemption",
      createDate: "2024-01-15T10:30:00Z",
    },
    {
      id: "review-2",
      title: "Nolan's Time-Bending Epic - Interstellar",
      body: "Christopher Nolan takes us on a visually stunning journey through space and time. The scientific accuracy combined with deeply human storytelling creates something truly special. Hans Zimmer's score elevates every scene to another level. McConaughey's performance is heartbreaking...",
      reputation: 342,
      messageCount: 67,
      userVoteId: 1,
      authorId: "user-123",
      authorName: "MovieBuff",
      authorImage: "https://picsum.photos/seed/user1/150/150",
      authorContentRating: 10,
      targetContentId: "content-9",
      targetContentTitle: "Interstellar",
      createDate: "2024-02-10T14:20:00Z",
    },
    {
      id: "review-3",
      title: "The Dark Knight - Redefining Superhero Cinema",
      body: "Heath Ledger's Joker is one of the greatest villain performances in cinema history. Nolan transforms a comic book movie into a crime thriller that transcends the genre. The moral complexity and philosophical questions raised make this more than just entertainment...",
      reputation: 523,
      messageCount: 123,
      userVoteId: 1,
      authorId: "user-123",
      authorName: "MovieBuff",
      authorImage: "https://picsum.photos/seed/user1/150/150",
      authorContentRating: 10,
      targetContentId: "content-3",
      targetContentTitle: "The Dark Knight",
      createDate: "2024-03-05T16:45:00Z",
    },
    {
      id: "review-4",
      title: "Fight Club - A Cultural Phenomenon",
      body: "David Fincher's adaptation of Chuck Palahniuk's novel is a scathing critique of consumer culture wrapped in a thrilling narrative. Edward Norton and Brad Pitt have incredible chemistry. The twist ending is legendary, but it's the journey that makes this film unforgettable...",
      reputation: 387,
      messageCount: 94,
      userVoteId: 1,
      authorId: "user-123",
      authorName: "MovieBuff",
      authorImage: "https://picsum.photos/seed/user1/150/150",
      authorContentRating: 9,
      targetContentId: "content-7",
      targetContentTitle: "Fight Club",
      createDate: "2024-04-12T11:15:00Z",
    },
  ],
};

/**
 * Get mock user details by username
 */
export function getMockUserDetails(userName: string): UserDetailsResponse {
  return {
    ...MOCK_USER_DETAILS,
    targetUserName: userName,
  };
}

/**
 * Get mock user followers
 */
export function getMockUserFollowers(
  _userName: string,
): UserFollowersResponse {
  return MOCK_USER_FOLLOWERS;
}

/**
 * Get mock user following
 */
export function getMockUserFollowing(
  _userName: string,
): UserFollowingResponse {
  return MOCK_USER_FOLLOWING;
}
